/* eslint-disable no-multiple-empty-lines */
import { html, nothing } from 'lit';
import { api, converse, _converse } from '@converse/headless';
import { shouldRenderMediaFromURL } from '../../../utils/url.js';
import { extractMapFromBody, latLngToTile, latLngToPixel, tileUrl } from '../../../utils/map.js';
import { extractConferenceUrl, isConferenceStartMessage, getConferenceUrl, getOwnDisplayName } from '../../../utils/conference.js';
import { getAuthorStyle } from '../../../utils/color.js';
import { getHats, getEmojiMarkup } from '../utils.js';
import { isSelectingMessages, isMessageSelected, toggleMessageSelection } from '../selection.js';
import { __ } from 'i18n';
import 'shared/avatar/avatar.js';
import 'shared/chat/unfurl.js';
import 'shared/chat/reaction-panel.js';

const { dayjs } = converse.env;

/**
 * @param {import('../message').default} el
 */
export default (el) => {
    const i18n_new_messages = __('New messages');

    const edited = el.model.get('edited');
    const is_first_unread = el.model_with_messages.get('first_unread_id') === el.model.get('id');
    const is_followup = el.model.isFollowup();
    const is_me_message = el.model.isMeCommand();
    const is_retracted = el.model.isRetracted();
    const msgid = el.model.get('msgid');
    const sender = el.model.get('sender');
    const time = el.model.get('time');

    const contact = el.model.occupant || el.model.contact;
    const author_style = getAuthorStyle(contact);
    const format = api.settings.get('time_format');

    const dayjs_time = dayjs(edited || time);
    const pretty_time = dayjs_time.format(format);
    const pretty_date = dayjs_time.format('llll');

    const hats = getHats(el.model);
    const username = el.model.getDisplayName();

    const is_action = is_me_message || is_retracted;
    // const should_show_header = !is_action && !is_followup;
    // We want to show the header for followup messages as well, because it contains the badges and the time of the message, which are relevant information for followup messages.
    const should_show_header = !is_action;
    const should_show_avatar = el.shouldShowAvatar() && should_show_header;
    const reactions = el.model.get('reactions') || {};
    const hasReactions = Object.keys(reactions).length > 0;
    const my_jid = _converse.bare_jid;
    const _mapBody = el.model.get('is_encrypted') ? el.model.get('plaintext') : el.model.get('body');
    const mapPreview = extractMapFromBody(_mapBody);
    const mucChatbox = el.model.collection?.chatbox;
    // Older messages still have the join URL embedded in the body (extractConferenceUrl
    // catches those); current ones don't (see heading.js), so derive it locally instead.
    const conferenceUrl = extractConferenceUrl(_mapBody) ||
        (isConferenceStartMessage(_mapBody) ? getConferenceUrl(mucChatbox?.get('jid')) : null);
    const bodyIsMapUrl = mapPreview && _mapBody?.trim() === mapPreview.url;
    const mapCoords = mapPreview?.lat !== null && mapPreview?.lat !== undefined
        ? /** @type {{ lat: number, lng: number, url: string }} */ (mapPreview)
        : null;
    const mapTiles = (() => {
        if (!mapCoords) return /** @type {Array<{src:string,style:string}>} */ ([]);
        const zoom = 14, W = 400, H = 200;
        const { x: tx, y: ty } = latLngToTile(mapCoords.lat, mapCoords.lng, zoom);
        const { x: px, y: py } = latLngToPixel(mapCoords.lat, mapCoords.lng, zoom, tx, ty);
        const offL = Math.round(W / 2 - px);
        const offT = Math.round(H / 2 - py);
        return [
            [tx - 1, ty - 1], [tx, ty - 1], [tx + 1, ty - 1],
            [tx - 1, ty],     [tx, ty],     [tx + 1, ty],
            [tx - 1, ty + 1], [tx, ty + 1], [tx + 1, ty + 1],
        ].map(([x, y]) => ({
            src: tileUrl(zoom, x, y),
            style: `position:absolute;width:256px;height:256px;left:${offL + (x - tx + 1) * 256 - 256}px;top:${offT + (y - ty + 1) * 256 - 256}px;`,
        }));
    })();
    // The model to use for the avatar.
    // Note: it can happen that the contact has not the vcard attribute but the message has.
    const avatar_model = contact?.vcard ? contact : el.model;

    const chatbox = el.model_with_messages;
    const is_selecting = isSelectingMessages(chatbox);
    const is_selected = is_selecting && isMessageSelected(chatbox, msgid);
    const onSelectToggle = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        toggleMessageSelection(chatbox, msgid);
    };

    return html`${is_first_unread
            ? html`<div class="message separator"><hr class="separator"><span class="separator-text">${i18n_new_messages}</span></div>`
            : ''}<div class="message chat-msg ${el.getExtraMessageClasses()} ${is_selecting ? 'chat-msg--selecting' : ''}" data-isodate="${time}" data-msgid="${msgid}" data-from="${el.model.get('from')}" data-encrypted="${el.model.get('is_encrypted')}"><a id="${msgid}"></a> ${is_selecting
                ? html`<div class="chat-msg__select-overlay" @click="${onSelectToggle}"><div class="chat-msg__select-circle ${is_selected ? 'is-selected' : ''}">${is_selected ? html`<i class="fa fa-check"></i>` : ''}</div></div>`
                : ''} ${should_show_avatar
                ? html`<a class="show-msg-author-modal" @click="${el.showUserModal}"><converse-avatar .model="${avatar_model}" class="avatar align-self-center" name="${el.model.getDisplayName()}" nonce="${avatar_model.vcard?.get('vcard_updated')}" height="40" width="40"></converse-avatar></a>`
                : ''}<div class="chat-msg__content chat-msg__content--${sender} ${is_action ? 'chat-msg__content--action' : ''}">${should_show_header
                    ? html`<span class="chat-msg__heading"><span class="chat-msg__author"><a class="show-msg-author-modal" @click="${el.showUserModal}" style="${author_style}">${username}</a> </span>${hats.map((h) => html`<span class="badge badge-secondary">${h.title}</span>`)} <time title="${pretty_date}" timestamp="${edited || time}" class="chat-msg__time">${pretty_time}</time> ${el.model.get('is_encrypted')
                              ? html`<converse-icon class="fa fa-lock" size="1.1em"></converse-icon>`
                              : ''}</span>`
                    : ''}<div class="chat-msg__body chat-msg__body--${el.model.get('message_type')} ${el.model.get('displayed') ? 'chat-msg__body--displayed' : ''} ${el.model.get('received') ? 'chat-msg__body--received' : ''} ${el.model.get('is_delayed') ? 'chat-msg__body--delayed' : ''}"><div class="chat-msg__message">${is_action
                            ? html`<time title="${pretty_date}" timestamp="${edited || time}" class="chat-msg__time">${pretty_time}</time> ${is_me_message
                                      ? html`<span class="chat-msg__author" style="${author_style}">${is_me_message ? '**' : ''}${username}</span> `
                                      : ''}`
                            : ''} ${is_retracted ? el.renderRetraction() : bodyIsMapUrl
                            ? html`<a href="${mapPreview.url}" target="_blank" rel="noopener" style="display:block;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;max-width:380px;font-size:.85em;opacity:.85;color:inherit">${mapPreview.url.replace(/^https?:\/\//, '').substring(0, 55)}${ mapPreview.url.length > 61 ? '…' : ''}</a>`
                            : el.renderMessageText()} ${hasReactions
                            ? html`<div class="chat-msg__reactions" style="display:flex;flex-wrap:wrap;gap:4px;margin-top:8px">${Object.entries(reactions).map(([emoji, jids]) => {
                                          const is_my_reaction = jids.includes(my_jid);

                                          return html`<div class="reaction-item ${is_my_reaction ? 'reaction-item--mine' : ''}" title="${jids.join(', ')}" style="background:${is_my_reaction ? 'rgba(0, 136, 204, 0.15)' : 'rgba(0,0,0,0.07)'};border-radius:8px;padding:2px 6px;font-size:.9em;cursor:pointer;display:flex;align-items:center;gap:4px;border:1px solid ${is_my_reaction ? '#0088cc' : 'rgba(0,0,0,0.1)'}" @click="${() => el.toggleReaction(emoji)}"><span>${getEmojiMarkup({
                                                          emoji: emoji,
                                                          cp: emoji.codePointAt(0).toString(16),
                                                          shortname: '',
                                                      })}</span><span style="font-weight:700;opacity:.7;color:inherit">${jids.length}</span></div>`;
                                      })}</div>`
                            : ''} ${mapCoords
                            ? html`<div style="margin-top:8px"><a href="${mapCoords.url}" target="_blank" rel="noopener noreferrer" style="display:block;width:400px;max-width:100%;height:200px;position:relative;overflow:hidden;border-radius:8px;border:1px solid rgba(0,0,0,.12);cursor:pointer">${mapTiles.map((t) => html`<img src="${t.src}" style="${t.style}" draggable="false">`)}<div style="position:absolute;left:200px;top:100px;transform:translate(-50%,-100%);font-size:28px;z-index:10;filter:drop-shadow(0 1px 3px rgba(0,0,0,.6));pointer-events:none">📍</div></a></div>`
                            : mapPreview
                            ? html`<div style="margin-top:6px"><a href="${mapPreview.url}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:8px;background:rgba(0,0,0,.07);text-decoration:none;font-size:.9em;color:inherit;border:1px solid rgba(0,0,0,.12)">📍 Відкрити карту</a></div>`
                            : ''} ${conferenceUrl
                            ? html`<div style="margin-top:8px;border-radius:12px;overflow:hidden;border:1px solid rgba(80,130,255,.3);background:rgba(20,40,90,.7);max-width:340px"><div style="padding:12px 14px 8px;display:flex;align-items:center;gap:10px"><svg width="32" height="32" viewBox="0 0 40 40" fill="none"><path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#253E75"/><path d="M24.278 17.7433C24.278 16.8683 24.2775 16.2611 24.2393 15.7892C24.2017 15.3269 24.1316 15.0668 24.0334 14.8727C23.8186 14.4483 23.476 14.1034 23.0546 13.8871C22.8618 13.7882 22.6035 13.7175 22.1445 13.6797C21.6759 13.6412 21.0729 13.6407 20.2039 13.6407H14.7034C13.8344 13.6407 13.2314 13.6412 12.7628 13.6797C12.3038 13.7175 12.0455 13.7882 11.8527 13.8871C11.4313 14.1034 11.0887 14.4483 10.874 14.8727C10.7757 15.0668 10.7056 15.3269 10.6681 15.7892C10.6298 16.2611 10.6293 16.8683 10.6293 17.7433V22.2567C10.6293 23.1317 10.6298 23.7389 10.6681 24.2108C10.7056 24.6731 10.7757 24.9332 10.874 25.1273C11.0887 25.5517 11.4313 25.8966 11.8527 26.1129C12.0455 26.2118 12.3038 26.2825 12.7628 26.3203C13.2314 26.3588 13.8344 26.3593 14.7034 26.3593H20.2039C21.0729 26.3593 21.6759 26.3588 22.1445 26.3203C22.6035 26.2825 22.8618 26.2118 23.0546 26.1129C23.476 25.8966 23.8186 25.5517 24.0334 25.1273C24.1316 24.9332 24.2017 24.6731 24.2393 24.2108C24.2775 23.7389 24.278 23.1317 24.278 22.2567V17.7433ZM26.2445 20L29.3707 23.1471V16.8529L26.2445 20ZM31 23.1471V16.8529L26.2445 20L29.3707 23.1471ZM31 16.8529V23.1471C31 23.4414 31.0012 23.7182 30.9821 23.9364C30.9652 24.1277 30.9223 24.4731 30.6827 24.7557C30.4075 25.0802 29.9999 25.2528 29.5726 25.2195C29.2048 25.1903 28.9321 24.9762 28.7859 24.8529C28.619 24.7122 28.4247 24.5162 28.2179 24.308L25.9073 21.9802V22.2567C25.9073 23.1046 25.9085 23.7899 25.8635 24.3441C25.8178 24.9079 25.7204 25.4085 25.4856 25.8725C25.1145 26.6056 24.5226 27.2017 23.7946 27.5753C23.3338 27.8118 22.8367 27.9099 22.2768 27.9559C21.7265 28.0012 21.0459 28 20.2039 28H14.7034C13.8614 28 13.1809 28.0012 12.6305 27.9559C12.0706 27.9099 11.5735 27.8118 11.1127 27.5753C10.3847 27.2017 9.79276 26.6056 9.42174 25.8725C9.18694 25.4085 9.08952 24.9079 9.04377 24.3441C8.99882 23.7899 9 23.1046 9 22.2567V17.7433C9 16.8954 8.99882 16.2101 9.04377 15.6559C9.08952 15.0921 9.18694 14.5915 9.42174 14.1275C9.79276 13.3944 10.3847 12.7983 11.1127 12.4247C11.5735 12.1882 12.0706 12.0901 12.6305 12.0441C13.1809 11.9988 13.8614 12 14.7034 12H20.2039C21.0459 12 21.7265 11.9988 22.2768 12.0441C22.8367 12.0901 23.3338 12.1882 23.7946 12.4247C24.5226 12.7983 25.1145 13.3944 25.4856 14.1275C25.7204 14.5915 25.8178 15.0921 25.8635 15.6559C25.9085 16.2101 25.9073 16.8954 25.9073 17.7433V18.0188L28.2179 15.692C28.4247 15.4838 28.619 15.2878 28.7859 15.1471C28.9321 15.0238 29.2048 14.8097 29.5726 14.7805C30.0433 14.7903 30.3425 14.915 30.5733 15.1291L30.6827 15.2443L30.7642 15.3524C30.933 15.6105 30.9674 15.8963 30.9821 16.0636C31.0012 16.2818 31 16.5587 31 16.8529Z" fill="#9AB9FF"/></svg><div><div style="font-size:14px;font-weight:600;color:#c5d5f0">Відеоконференція</div></div></div><div style="padding:0 14px 12px"><button @click="${() => {
                                              const el2 = /** @type {any} */ (document.createElement('converse-jitsi-conference'));
                                              el2.room = conferenceUrl.split('/').pop();
                                              el2.mucJid = mucChatbox?.get('jid') || '';
                                              el2.displayName = getOwnDisplayName(_converse.bare_jid?.split('@')[0] || '');
                                              // Bug 3: pass group name so Jitsi shows it as conference title
                                              el2.subject = (typeof mucChatbox?.getDisplayName === 'function'
                                                  ? mucChatbox.getDisplayName()
                                                  : null) || mucChatbox?.get('name') || el2.room;
                                              document.body.appendChild(el2);
                                          }}" style="width:100%;padding:8px;border-radius:8px;border:none;background:#3a6fd8;color:#fff;font-size:14px;font-weight:600;cursor:pointer">Приєднатися</button></div></div>`
                            : ''}</div><converse-message-actions .model="${el.model}" ?is_retracted="${is_retracted}"><div class="chat-msg__action-react-wrapper"><button class="btn btn--transparent chat-msg__action-react-btn" title="${__('Add reaction')}" @click="${(e) => {
                                    e.stopPropagation();
                                    const btn = /** @type {HTMLElement} */ (e.currentTarget);
                                    const btnRect = btn.getBoundingClientRect();
                                    const isMe = !!btn.closest('.chat-msg__content--me');
                                    document.querySelector('converse-reaction-panel')?.remove();
                                    const panel = /** @type {any} */ (document.createElement('converse-reaction-panel'));
                                    panel.model = el.model;
                                    document.body.appendChild(panel);
                                    panel.updateComplete.then(() =>
                                        requestAnimationFrame(() => panel.positionNear(btnRect, isMe))
                                    );
                                }}"><converse-icon class="far fa-smile" color="var(--foreground-color)" size="1em"></converse-icon></button></div></converse-message-actions></div><div class="chat-msg__footer"><time title="${pretty_date}" timestamp="${edited || time}" class="chat-msg__time">${pretty_time}</time> ${el.model.get('is_encrypted')
                        ? html`<converse-icon class="fa fa-lock" size="1.1em"></converse-icon>`
                        : ''}</div>${el.model.get('ogp_metadata')?.map((m) => {
                    if (el.model.get('hide_url_previews') === true) {
                        return '';
                    }
                    // The conference card above already covers this URL with a
                    // localized, styled preview. The generic unfurl arrives
                    // later (async server-side fastening stanza) and prints
                    // whatever raw og:title the Jitsi page itself returns
                    // (e.g. the literal English "JOIN" button label) — showing
                    // both looks like a stray, unstyled, unlocalized duplicate.
                    if (conferenceUrl && m['og:url']?.replace(/\/$/, '') === conferenceUrl.replace(/\/$/, '')) {
                        return '';
                    }
                    return html`<converse-message-unfurl @animationend="${el.onUnfurlAnimationEnd}" class="${el.model.get('url_preview_transition')}" jid="${el.model_with_messages?.get('jid')}" description="${m['og:description'] || ''}" title="${m['og:title'] || ''}" image="${m['og:image'] && shouldRenderMediaFromURL(m['og:image'], 'image')
                            ? m['og:image']
                            : nothing}" site_name="${m['og:site_name'] || ''}" url="${m['og:url'] || ''}"></converse-message-unfurl>`;
                })}</div></div>`;
};

// eslint-disable-next-line no-multiple-empty-lines

// import { html, nothing } from 'lit';
// import { api, converse, _converse } from '@converse/headless';
// import { shouldRenderMediaFromURL } from '../../../utils/url.js';
// import { getAuthorStyle } from '../../../utils/color.js';
// import { getHats, getEmojiMarkup } from '../utils.js';
// import { __ } from 'i18n';
// import 'shared/avatar/avatar.js';
// import 'shared/chat/unfurl.js';

// const { dayjs } = converse.env;

// /**
//  * @param {import('../message').default} el
//  */
// export default (el) => {
//     const i18n_new_messages = __('New messages');

//     const edited = el.model.get('edited');
//     const is_first_unread = el.model_with_messages.get('first_unread_id') === el.model.get('id');
//     const is_followup = el.model.isFollowup();
//     const is_me_message = el.model.isMeCommand();
//     const is_retracted = el.model.isRetracted();
//     const msgid = el.model.get('msgid');
//     const sender = el.model.get('sender');
//     const time = el.model.get('time');

//     const contact = el.model.occupant || el.model.contact;
//     const author_style = getAuthorStyle(contact);
//     const format = api.settings.get('time_format');

//     const dayjs_time = dayjs(edited || time);
//     const pretty_time = dayjs_time.format(format);
//     const pretty_date = dayjs_time.format('llll');

//     const hats = getHats(el.model);
//     const username = el.model.getDisplayName();

//     const is_action = is_me_message || is_retracted;
//     // const should_show_header = !is_action && !is_followup;
//     // We want to show the header for followup messages as well, because it contains the badges and the time of the message, which are relevant information for followup messages.
//     const should_show_header = !is_action;
//     const should_show_avatar = el.shouldShowAvatar() && should_show_header;
//     const reactions = el.model.get('reactions') || {};
//     const hasReactions = Object.keys(reactions).length > 0;
//     const my_jid = _converse.bare_jid;
//     // The model to use for the avatar.
//     // Note: it can happen that the contact has not the vcard attribute but the message has.
//     const avatar_model = contact?.vcard ? contact : el.model;

//     return html`${is_first_unread
//             ? html`<div class="message separator">
//                   <hr class="separator" />
//                   <span class="separator-text">${i18n_new_messages}</span>
//               </div>`
//             : ''}
//         <div
//             class="message chat-msg ${el.getExtraMessageClasses()}"
//             data-isodate="${time}"
//             data-msgid="${msgid}"
//             data-from="${el.model.get('from')}"
//             data-encrypted="${el.model.get('is_encrypted')}"
//         >
//             <!-- Anchor to allow us to scroll the message into view -->
//             <a id="${msgid}"></a>

//             ${should_show_avatar
//                 ? html`<a class="show-msg-author-modal" @click=${el.showUserModal}>
//                       <converse-avatar
//                           .model=${avatar_model}
//                           class="avatar align-self-center"
//                           name="${el.model.getDisplayName()}"
//                           nonce="${avatar_model.vcard?.get('vcard_updated')}"
//                           height="40"
//                           width="40"
//                       ></converse-avatar>
//                   </a>`
//                 : ''}

//             <div class="chat-msg__content chat-msg__content--${sender} ${is_action ? 'chat-msg__content--action' : ''}">
//                 ${should_show_header
//                     ? html` <span class="chat-msg__heading">
//                           <span class="chat-msg__author">
//                               <a class="show-msg-author-modal" @click=${el.showUserModal} style="${author_style}"
//                                   >${username}</a
//                               >
//                           </span>
//                           ${hats.map((h) => html`<span class="badge badge-secondary">${h.title}</span>`)}
//                           <time title="${pretty_date}" timestamp="${edited || time}" class="chat-msg__time"
//                               >${pretty_time}</time
//                           >
//                           ${el.model.get('is_encrypted')
//                               ? html`<converse-icon class="fa fa-lock" size="1.1em"></converse-icon>`
//                               : ''}
//                       </span>`
//                     : ''}

//                 <div
//                     class="chat-msg__body chat-msg__body--${el.model.get('message_type')}
//                 ${el.model.get('displayed') ? 'chat-msg__body--displayed' : ''}
//                 ${el.model.get('received') ? 'chat-msg__body--received' : ''}
//                 ${el.model.get('is_delayed') ? 'chat-msg__body--delayed' : ''}"
//                 >
//                     <div class="chat-msg__message">
//                         ${is_action
//                             ? html`<time title="${pretty_date}" timestamp="${edited || time}" class="chat-msg__time"
//                                       >${pretty_time}</time
//                                   >
//                                   ${is_me_message
//                                       ? html`<span class="chat-msg__author" style="${author_style}"
//                                                 >${is_me_message ? '**' : ''}${username}</span
//                                             >&nbsp;`
//                                       : ''}`
//                             : ''}
//                         ${is_retracted ? el.renderRetraction() : el.renderMessageText()}
//                         ${hasReactions
//                             ? html`
//                                   <div
//                                       class="chat-msg__reactions"
//                                       style="display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px;"
//                                   >
//                                       ${Object.entries(reactions).map(([emoji, jids]) => {
//                                           const is_my_reaction = jids.includes(my_jid);

//                                           return html`
//                                               <div
//                                                   class="reaction-item ${is_my_reaction ? 'reaction-item--mine' : ''}"
//                                                   title="${jids.join(', ')}"
//                                                   style="
//                 background: ${is_my_reaction ? 'rgba(0, 136, 204, 0.15)' : 'rgba(0,0,0,0.07)'};
//                 border-radius: 8px;
//                 padding: 2px 6px;
//                 font-size: 0.9em;
//                 cursor: pointer;
//                 display: flex;
//                 align-items: center;
//                 gap: 4px;
//                 border: 1px solid ${is_my_reaction ? '#0088cc' : 'rgba(0,0,0,0.1)'};
//              "
//                                                   @click=${() => el.toggleReaction(emoji)}
//                                               >
//                                                   <span
//                                                       >${getEmojiMarkup({
//                                                           emoji: emoji,
//                                                           cp: emoji.codePointAt(0).toString(16),
//                                                           shortname: '',
//                                                       })}</span
//                                                   >
//                                                   <span
//                                                       style="font-weight: bold; opacity: 0.7; color: ${is_my_reaction
//                                                           ? '#0088cc'
//                                                           : 'inherit'};"
//                                                   >
//                                                       ${jids.length}
//                                                   </span>
//                                               </div>
//                                           `;
//                                       })}
//                                   </div>
//                               `
//                             : ''}
//                     </div>
//                     <converse-message-actions
//                         .model=${el.model}
//                         ?is_retracted=${is_retracted}
//                     >
//                     </converse-message-actions>
//                 </div>
//                 <div class="chat-msg__footer">
//                     <time title="${pretty_date}" timestamp="${edited || time}" class="chat-msg__time"
//                         >${pretty_time}</time
//                     >
//                     ${el.model.get('is_encrypted')
//                         ? html`<converse-icon class="fa fa-lock" size="1.1em"></converse-icon>`
//                         : ''}
//                 </div>

//                 ${el.model.get('ogp_metadata')?.map((m) => {
//                     if (el.model.get('hide_url_previews') === true) {
//                         return '';
//                     }
//                     return html`<converse-message-unfurl
//                         @animationend="${el.onUnfurlAnimationEnd}"
//                         class="${el.model.get('url_preview_transition')}"
//                         jid="${el.model_with_messages?.get('jid')}"
//                         description="${m['og:description'] || ''}"
//                         title="${m['og:title'] || ''}"
//                         image="${m['og:image'] && shouldRenderMediaFromURL(m['og:image'], 'image')
//                             ? m['og:image']
//                             : nothing}"
//                         site_name="${m['og:site_name'] || ''}"
//                         url="${m['og:url'] || ''}"
//                     ></converse-message-unfurl>`;
//                 })}
//             </div>
//         </div>`;
// };
